/**
 * HUD.ts — top heads-up display for the Painting scene.
 *
 * Left: a back/gallery chip. Center: picture title + cell progress bar with
 * teal fill. Right: animated balance pill (gold coin + count-up). Below the bar:
 * the picture-bonus FOMO meter (owned by FomoHud, mounted by the scene).
 */
import { Container, Graphics } from 'pixi.js';
import { Button, glassPanel, makeText } from './widgets';
import theme, { hex } from '../render/theme';
import { money } from '../util/format';
import { Tweens } from '../util/tween';
import { easeOutCubic, easeOutQuart } from '../util/ease';
import type { Viewport } from '../app/AppContext';

export class HUD extends Container {
  private bar = new Container();
  private panel: Graphics;
  private title;
  private progressTrack: Graphics;
  private progressFill: Graphics;
  private progressLabel;
  private balancePill: Graphics;
  private balanceText;
  private coin: Graphics;
  private backBtn: Button;

  private barW = 0;
  private displayedBalance = 0;
  private progress = 0;
  private pillRect = { x: 0, y: 0, w: 0, h: 0 };
  private flash = new Graphics();

  private netPL = 0;
  private netLabel?: ReturnType<typeof makeText>;
  private netBg = new Graphics();
  private infoBtn?: Button;
  private funBadge?: Container;
  private netRight = 52; // right edge of the net pill — progress bar starts after it
  private fullTitle = '';

  /** Panel height — single source of truth so the scene below stacks without a magic number. */
  get panelHeight(): number { return 100; }

  constructor(opts: { title: string; balance: number; onBack: () => void; onInfo?: () => void; funMode?: boolean }) {
    super();
    this.panel = glassPanel(300, 120);
    this.addChild(this.panel);

    this.backBtn = new Button({
      width: 44,
      height: 44,
      label: '‹',
      fill: theme.color.white,
      textColor: theme.color.ink,
      fontSize: 28,
      radius: 22,
      onTap: opts.onBack,
    });
    this.addChild(this.backBtn);

    if (opts.onInfo) {
      this.infoBtn = new Button({
        width: 32, height: 32, label: 'i', fontSize: 17, radius: 16,
        fill: theme.color.bgLight3, textColor: theme.color.ink,
        onTap: opts.onInfo,
      });
      this.addChild(this.infoBtn);
    }
    if (opts.funMode) {
      this.funBadge = new Container();
      const t = makeText('FUN', { size: 10, weight: '800', color: theme.color.white, letterSpacing: 0.5 });
      const g = new Graphics();
      const w = t.width + 14;
      g.roundRect(0, 0, w, 18, 9).fill(hex(theme.palette.coral));
      t.position.set(7, 4);
      this.funBadge.addChild(g, t);
      this.addChild(this.funBadge);
    }

    // always-on net P/L pill below the title; honest framing (UKGC: net visible on demand,
    // here we make it always visible)
    this.netLabel = makeText('Net  $0.00', { size: 12, weight: '800', color: theme.color.muted });
    this.addChild(this.netBg, this.netLabel);

    this.fullTitle = opts.title;
    this.title = makeText(opts.title, { size: 17, weight: '800', color: theme.color.ink });
    this.addChild(this.title);

    // balance pill
    this.balancePill = new Graphics();
    this.coin = new Graphics();
    this.balanceText = makeText(money(opts.balance), {
      size: 17,
      weight: '800',
      color: theme.color.goldText,
    });
    this.displayedBalance = opts.balance;
    this.addChild(this.balancePill, this.flash, this.coin, this.balanceText);

    // progress
    this.progressTrack = new Graphics();
    this.progressFill = new Graphics();
    this.progressLabel = makeText('0%', { size: 12, weight: '700', color: theme.color.muted });
    this.addChild(this.progressTrack, this.progressFill, this.progressLabel);

    this.addChild(this.bar);
  }

  setTitle(s: string): void {
    this.fullTitle = s;
    this.title.text = s;
  }

  /** Single-line truncate the title with an ellipsis so it never runs under the FUN chip / pill. */
  private clampTitle(maxW: number): void {
    this.title.text = this.fullTitle;
    if (this.title.width <= maxW || this.fullTitle.length < 2) return;
    let s = this.fullTitle;
    while (s.length > 1) {
      s = s.slice(0, -1);
      this.title.text = s + '…';
      if (this.title.width <= maxW) break;
    }
  }

  /**
   * Animate balance to a new value with a count-up + gold flash on increase.
   * The roll uses easeOutQuart so the LAST cents flow visibly (odometer-style climb) instead of
   * a flat constant-speed swap — the feel-review minor "balance digits swap flatly" gap.
   */
  setBalance(v: number): void {
    const from = this.displayedBalance;
    const up = v > from;
    Tweens.to({
      from,
      to: v,
      duration: theme.motion.slow + Math.min(400, Math.abs(v - from) * 8),
      ease: easeOutQuart,
      onUpdate: (val) => {
        this.displayedBalance = val;
        this.balanceText.text = money(val);
        this.layoutBalance();
      },
    });
    if (up) {
      this.coin.scale.set(1.3);
      Tweens.to({
        from: 1.3,
        to: 1,
        duration: theme.motion.base,
        ease: easeOutCubic,
        onUpdate: (s) => this.coin.scale.set(s),
      });
      this.flashBalance();
    }
  }

  /** Scene-space centre of the balance coin (so the drip coins know where to fly). */
  balanceScreenPos(): { x: number; y: number } {
    return { x: this.x + this.coin.x, y: this.y + this.coin.y };
  }

  /** A gold pulse around the balance pill — the "win landed here" beat. */
  flashBalance(): void {
    const { x, y, w, h } = this.pillRect;
    Tweens.to({
      from: 0, to: 1, duration: 520, ease: easeOutCubic,
      onUpdate: (t) => {
        if (this.flash.destroyed) return;
        this.flash.clear();
        const pad = 2 + t * 8;
        this.flash
          .roundRect(x - pad, y - pad, w + pad * 2, h + pad * 2, (h + pad * 2) / 2)
          .stroke({ width: 3 * (1 - t) + 0.5, color: hex(theme.palette.gold1), alpha: 0.85 * (1 - t) });
      },
      onComplete: () => { if (!this.flash.destroyed) this.flash.clear(); },
    });
  }

  /** A quick squash on the coin when a flying drip-coin is "caught". */
  catchCoin(): void {
    this.coin.scale.set(1.42);
    Tweens.to({
      from: 1.42, to: 1, duration: theme.motion.base, ease: easeOutCubic,
      onUpdate: (s) => { if (!this.coin.destroyed) this.coin.scale.set(s); },
    });
    this.flashBalance();
  }

  /** Net session P/L pill — paid minus wagered. Negative = red, positive = teal. */
  setNetPL(net: number): void {
    this.netPL = net;
    this.layoutNetPL();
  }

  private layoutNetPL(): void {
    if (!this.netLabel) return;
    const sign = this.netPL >= 0 ? '+' : '−';
    const abs = Math.abs(this.netPL);
    this.netLabel.text = `Net ${sign}${money(abs)}`;
    const c = this.netPL >= 0 ? theme.color.teal : theme.color.coral;
    this.netLabel.style.fill = c;
    const w = this.netLabel.width + 20;
    const x = 52;        // after the 32px info button at x14
    const y = 62;
    const h = 22;
    this.netBg.clear();
    this.netBg.roundRect(x, y, w, h, h / 2).fill({ color: theme.color.bgLight3, alpha: 0.95 });
    this.netBg.roundRect(x, y, w, h, h / 2).stroke({ width: 1, color: c, alpha: 0.55 });
    this.netLabel.position.set(x + 10, y + 5);
    this.netRight = x + w;
  }

  /** progress 0..1 across all cells. */
  setProgress(p: number): void {
    const target = Math.max(0, Math.min(1, p));
    Tweens.to({
      from: this.progress,
      to: target,
      duration: theme.motion.base,
      ease: easeOutCubic,
      onUpdate: (v) => {
        this.progress = v;
        this.redrawProgress();
      },
    });
  }

  layout(v: Viewport): void {
    const pad = 14;
    const top = v.insetTop + 10;
    const w = v.width - pad * 2;
    const panelH = this.panelHeight;
    this.barW = w;
    this.position.set(pad, top);
    this.panel.clear();
    this.panel
      .roundRect(0, 0, w, panelH, theme.radius.card)
      .fill({ color: theme.color.white, alpha: theme.color.glassAlpha });
    this.panel
      .roundRect(0, 0, w, panelH, theme.radius.card)
      .stroke({ width: 1, color: theme.color.white, alpha: 0.9 });

    // --- Row 1: back · title · FUN chip · balance ---
    this.backBtn.position.set(12, 12);
    this.layoutBalance();    // sets pillRect (needed to clamp the title width)
    const funW = this.funBadge ? this.funBadge.width + 8 : 0;
    const titleMax = this.pillRect.x - 12 - 66 - funW;
    this.clampTitle(titleMax);
    this.title.position.set(66, 19);
    if (this.funBadge) {
      this.funBadge.position.set(66 + this.title.width + 8, 22);
    }

    // --- Row 2: info · Net pill · progress bar · % ---
    if (this.infoBtn) this.infoBtn.position.set(14, 58);
    this.layoutNetPL();      // sets this.netRight
    this.redrawProgress();   // reads this.netRight
  }

  private layoutBalance(): void {
    const w = this.barW;
    const tw = this.balanceText.width;
    const pillW = tw + 56;
    const x = w - pillW - 12;
    const y = 14;
    const h = 40;
    this.pillRect = { x, y, w: pillW, h };
    this.balancePill.clear();
    this.balancePill.roundRect(x, y, pillW, h, h / 2).fill(hex(theme.palette.gold1));
    this.balancePill.roundRect(x, y, pillW, h, h / 2).stroke({ width: 1.5, color: hex(theme.palette.gold2), alpha: 0.5 });
    // coin
    const cr = 13;
    this.coin.clear();
    this.coin.circle(0, 0, cr).fill(hex(theme.palette.gold2));
    this.coin.circle(0, 0, cr * 0.8).fill(hex(theme.palette.white));
    this.coin.circle(0, 0, cr * 0.8).stroke({ width: 1.5, color: hex(theme.palette.gold2), alpha: 0.5 });
    this.coin.position.set(x + 22, y + h / 2);
    this.balanceText.position.set(x + 40, y + h / 2 - this.balanceText.height / 2);
  }

  private redrawProgress(): void {
    const w = this.barW;
    const x = this.netRight + 12;     // begins after the Net pill
    const y = 69;
    const bh = 6;                     // SLIM — a sub-indicator, distinct from the heavier FOMO meter
    const bw = Math.max(48, w - x - 44); // floored so a wide Net value can't drive the track negative
    this.progressTrack.clear();
    this.progressTrack.roundRect(x, y, bw, bh, bh / 2).fill({ color: theme.color.bgLight3, alpha: 1 });
    this.progressFill.clear();
    const fw = Math.max(0, bw * this.progress);
    if (fw > 2) this.progressFill.roundRect(x, y, fw, bh, bh / 2).fill(hex(theme.palette.teal));
    this.progressLabel.text = Math.round(this.progress * 100) + '%';
    this.progressLabel.position.set(x + bw + 8, y - 5);
  }
}
